import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { OrderPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Breadcrumbs } from "@/components/breadcrumbs";

function fmtMoney(cents: number, currency: string): string {
  return `${currency === "ZAR" ? "R " : `${currency} `}${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}

export default function MyOrdersPage() {
  const [orders, setOrders] = useState<OrderPublic[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getMyOrders()
      .then((r) => setOrders(r.orders))
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"));
  }, []);

  if (error) {
    return <p className="text-sm text-destructive" data-page="my-orders" data-state="error" data-error>{error}</p>;
  }
  if (orders === null) {
    return <p className="text-muted-foreground" data-page="my-orders" data-state="loading">Loading…</p>;
  }

  return (
    <div data-page="my-orders" data-state="ready" className="flex flex-col gap-6">
      <Breadcrumbs items={[{ label: "Events", to: "/discover" }, { label: "Shop", to: "/shop" }, { label: "My Orders" }]} />
      <div>
        <h1 className="text-2xl font-semibold">My orders</h1>
        <p className="text-muted-foreground">Orders you've placed through the Dugads Shop. Providers invoice and deliver directly.</p>
      </div>

      {orders.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">No orders yet. <Link to="/shop" className="text-primary hover:underline">Visit the Shop</Link>.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm" data-region="orders">
              <thead>
                <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-2 px-4 text-left">Order</th>
                  <th className="py-2 px-4 text-left">Provider</th>
                  <th className="py-2 px-4 text-left">Status</th>
                  <th className="py-2 px-4 text-left">Date</th>
                  <th className="py-2 px-4 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-border/60 hover:bg-muted/30" data-item="order" data-item-id={o.id}>
                    <td className="py-3 px-4">
                      <Link to={`/me/orders/${o.slug}`} className="font-medium text-primary hover:underline">#{o.slug}</Link>
                    </td>
                    <td className="py-3 px-4">{o.provider.name}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs uppercase text-muted-foreground">{o.status}</span>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{fmtDate(o.submitted_at)}</td>
                    <td className="py-3 px-4 text-right font-medium tabular-nums">{fmtMoney(o.total_cents, o.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
